import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";

import SMQMainContainer from "../../../components/SMQMainContainer";
import ConstatService from "../../../services/smq/ConstatService";
import SuiviService from "../../../services/smq/SuiviService";
import { getDisplayedNcStatus, isDeadlineExceeded } from "../NC/ncStatusHelper";

const NAVY = "#173d61";
const BORDER = "#cbd8e6";
const EMPTY_VALUE = "—";

const SHEET_CARDS = [
  {
    description: "Référence · Date · Processus · Qualification · Priorité · Pilote · Statut · Délai",
    icon: <ListAltOutlinedIcon />,
    title: "Feuille 1 — Registre NC",
  },
  {
    description: "Constat · Exigence · Action curative · Cause racine · Action corrective · Indicateur",
    icon: <DescriptionOutlinedIcon />,
    title: "Feuille 2 — Fiches complètes",
  },
  {
    description: "Problème de départ · Pourquoi · Familles 5M · Cause racine",
    icon: <AnalyticsOutlinedIcon />,
    title: "Feuille 3 — Analyse 5P 5M",
  },
  {
    description: "Actions correctives · Responsables · Délais · Indicateurs · Statut de suivi",
    icon: <PushPinOutlinedIcon />,
    title: "Feuille 4 — Plan d'actions",
  },
];

const STATUS_LABELS = {
  delai_depasse: "🚨 Délai dépassé",
  done: "✅ Clôturée",
  done_after_reopen: "✅ Clôturée après réouverture",
  in_progress: "🔄 En cours",
  open: "⏳ Ouverte",
  reopen: "🔄 NC réouverte — action insuffisante",
};

const CRITICALITY_LABELS = {
  critique: "🔴 Critique",
  majeure: "🟠 Majeure",
  mineure: "🟢 Mineure",
};

const cleanValue = (value) => {
  const text = String(value ?? "").trim();
  return text || null;
};

const statusKey = (row) =>
  row.status === "done" && row.has_reopened_child
    ? "done_after_reopen"
    : getDisplayedNcStatus(row.status, row.deadline);

const statusLabel = (row) => STATUS_LABELS[statusKey(row)] || row.status || EMPTY_VALUE;

const criticiteLabel = (value) => {
  const key = String(value || "").toLocaleLowerCase("fr");
  return CRITICALITY_LABELS[key] || cleanValue(value);
};

const latestSuivi = (detail) =>
  Array.isArray(detail.suivis) && detail.suivis.length ? detail.suivis[0] : null;

const joinValues = (values, separator = " | ") => {
  const uniqueValues = [...new Set(values.map(cleanValue).filter(Boolean))];
  return uniqueValues.length ? uniqueValues.join(separator) : null;
};

const FIVE_M_LABELS = {
  main_oeuvre: "Main-d'œuvre",
  materiel: "Matériel",
  matiere: "Matière",
  methode: "Méthode",
  milieu: "Milieu",
};

const formatFiveMFamilies = (value) => {
  const families = Array.isArray(value)
    ? value
    : String(value || "").split(",");

  return joinValues(
    families.map((family) => {
      const key = String(family || "").trim();
      return FIVE_M_LABELS[key] || key;
    }),
    ", "
  );
};

const addSheet = (workbook, name, rows, widths, headerRow) => {
  const worksheet = XLSX.utils.aoa_to_sheet(rows, { cellDates: true });
  worksheet["!cols"] = widths.map((width) => ({ wch: width }));
  worksheet["!autofilter"] = {
    ref: XLSX.utils.encode_range({
      e: { c: rows[headerRow - 1].length - 1, r: rows.length - 1 },
      s: { c: 0, r: headerRow - 1 },
    }),
  };
  XLSX.utils.book_append_sheet(workbook, worksheet, name);
};

const buildWorkbook = (details) => {
  const generatedDate = new Date().toLocaleDateString("fr-FR");
  const workbook = XLSX.utils.book_new();

  const registreRows = [
    ["Registre des Non-Conformités — FID"],
    [`Généré le : ${generatedDate}`],
    [],
    ["Référence", "Date", "Antenne/Service", "Processus", "Exigence", "Criticité (Matrice)", "Pilote", "Statut", "Délai", "⚠ Délai dépassé"],
    ...details.map((detail) => [
      detail.reference,
      detail.constat_date,
      detail.direction_code,
      detail.processus,
      detail.exigence_code,
      criticiteLabel(detail.criticite),
      detail.pilote_name || detail.constat_user_name,
      statusLabel(detail),
      detail.deadline,
      isDeadlineExceeded(detail.status, detail.deadline) ? "OUI" : "non",
    ]),
  ];
  addSheet(workbook, "Registre NC", registreRows, [17, 13, 23, 26, 19, 20, 23, 26, 15, 18], 4);

  const fullRows = [
    ["Fiches complètes — Non-Conformités FID"],
    [],
    ["Réf.", "Date", "Site", "Processus", "Constat", "Qualification", "Exigence", "Action curative", "Outil analyse", "Cause racine", "Actions correctives (résumé)", "Responsable plan", "Délai fin plan", "Indicateur", "Résultat vérif.", "Vérifié par", "Statut clôture", "Commentaire"],
    ...details.map((detail) => {
      const actions = Array.isArray(detail.actions_correctives) ? detail.actions_correctives : [];
      const suivi = latestSuivi(detail);
      return [
        detail.reference,
        detail.constat_date,
        detail.direction_code,
        detail.processus,
        detail.description_constat,
        joinValues([detail.trigger_code, detail.trigger_name], " — "),
        detail.description_exigence || joinValues([detail.exigence_code, detail.exigence_name], " — "),
        detail.curatif?.description,
        detail.analyse ? "N Pourquoi + 5M" : null,
        detail.analyse?.cause_racine,
        joinValues(actions.map((action) => joinValues([action.action_corrective, action.plan_action]))),
        joinValues(actions.map((action) => action.responsable_name)),
        joinValues(actions.map((action) => action.date_fin)),
        joinValues(actions.map((action) => action.indicateur_efficacite)),
        suivi?.resultat_verification,
        suivi?.evaluateur_name,
        statusLabel(detail),
        suivi?.commentaire,
      ];
    }),
  ];
  addSheet(workbook, "Fiches complètes", fullRows, Array(18).fill(23), 3);

  const analysisData = details.flatMap((detail) => {
    const pourquois = Array.isArray(detail.analyse_pourquois)
      ? [...detail.analyse_pourquois].sort(
          (left, right) => Number(left?.ordre || 0) - Number(right?.ordre || 0)
        )
      : [];
    const rows = pourquois.length ? pourquois : [{}];

    return rows.map((pourquoi) => [
      detail.reference || EMPTY_VALUE,
      detail.processus || EMPTY_VALUE,
      detail.analyse_probleme_depart || EMPTY_VALUE,
      pourquoi.ordre || EMPTY_VALUE,
      pourquoi.texte || EMPTY_VALUE,
      formatFiveMFamilies(pourquoi.famille_5m) || EMPTY_VALUE,
      detail.analyse_cause_racine || EMPTY_VALUE,
    ]);
  });
  const analysisRows = [
    ["Analyse 5P 5M — FID SMQ"],
    [],
    ["Référence NC", "Processus", "Problème de départ", "N° Pourquoi", "Pourquoi", "Famille(s) 5M", "Cause racine"],
    ...analysisData,
  ];
  addSheet(workbook, "Analyse 5P 5M", analysisRows, [20, 25, 38, 13, 42, 25, 38], 3);

  const actionData = details.flatMap((detail) => {
    const actions = Array.isArray(detail.actions_correctives) ? detail.actions_correctives : [];
    const rows = actions.length ? actions : [{}];
    return rows.map((action) => [
      detail.reference,
      joinValues([action.action_corrective, action.plan_action]),
      action.responsable_name,
      action.date_debut,
      action.date_fin,
      action.indicateur_efficacite,
      statusLabel(detail),
    ]);
  });
  const actionRows = [
    ["Plan d'actions correctives — FID SMQ"],
    [],
    ["Référence NC", "Action corrective", "Responsable", "Date début", "Délai fin", "Indicateur", "Statut"],
    ...actionData,
  ];
  addSheet(workbook, "Plan d'actions", actionRows, [17, 41, 23, 13, 13, 31, 26], 3);

  workbook.Props = {
    Author: "FID — SMQ",
    CreatedDate: new Date(),
    Subject: "Registre complet des non-conformités",
    Title: "Registre des Non-Conformités — FID",
  };

  return workbook;
};

const ExportExcel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [messageOpen, setMessageOpen] = useState(false);
  const requestIdRef = useRef(0);

  const loadRows = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError(null);
    try {
      const payload = await ConstatService.getRegistre();
      if (!Array.isArray(payload?.data)) {
        throw new Error("Le service du registre a retourné une réponse invalide.");
      }
      if (requestId === requestIdRef.current) setRows(payload.data);
    } catch (loadError) {
      if (requestId === requestIdRef.current) {
        setRows([]);
        setError(loadError?.message || "Impossible de charger le registre.");
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadRows]);

  const downloadExcel = async () => {
    if (exporting) return;
    setExporting(true);
    setError(null);
    try {
      const detailPayloads = await Promise.all(
        rows.map((row) =>
          Promise.all([
            SuiviService.getDetail(row.constat_id),
            ConstatService.getConstat(row.constat_id),
          ])
        )
      );
      const details = detailPayloads.map(([suiviPayload, constatPayload], index) => {
        if (!suiviPayload?.data || !constatPayload?.data) {
          throw new Error(`La fiche ${rows[index].reference || rows[index].constat_id} est indisponible.`);
        }
        return {
          ...suiviPayload.data,
          analyse_cause_racine: constatPayload.data.analyse_cause_racine,
          analyse_pourquois: constatPayload.data.analyse_pourquois,
          analyse_probleme_depart: constatPayload.data.analyse_probleme_depart,
        };
      });
      const workbook = buildWorkbook(details);
      const dateKey = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `FID_NC_Registre_${dateKey}.xlsx`, { compression: true });
      setMessageOpen(true);
    } catch (exportError) {
      setError(exportError?.message || "Impossible de générer le fichier Excel.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <SMQMainContainer>
      <Box sx={{ backgroundColor: "#edf2f8", minHeight: "calc(100vh - 56px)", p: { xs: 1.5, sm: 2.5 } }}>
        <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: "10px", boxShadow: "0 2px 7px rgba(16,38,61,.05)", p: { xs: 1.5, sm: 2.5 } }}>
          <Box sx={{ alignItems: "center", borderBottom: `1px solid ${BORDER}`, display: "flex", gap: 1.2, pb: 1.5 }}>
            <Box sx={{ alignItems: "center", backgroundColor: "#e8f8f1", borderRadius: "8px", color: "#168a4d", display: "flex", height: 36, justifyContent: "center", width: 36 }}><DownloadOutlinedIcon fontSize="small" /></Box>
            <Box>
              <Typography color="#10263d" fontSize={15} fontWeight={700}>Exporter les données</Typography>
              <Typography color="#60758a" fontSize={11}>Générer un fichier Excel formaté — registre complet des NC</Typography>
            </Box>
          </Box>

          <Alert icon={<CheckCircleOutlineIcon />} severity="success" sx={{ border: "1px solid #5bd79b", mt: 2 }}>
            Le fichier Excel exporté contient <strong>4 feuilles</strong> : Registre NC · Fiches complètes · Analyse 5P 5M · Plan d'actions.
          </Alert>

          {error && <Alert action={loading ? null : <Button color="inherit" onClick={loadRows} size="small">Réessayer</Button>} severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}

          <Grid container spacing={1.5} sx={{ mt: 0.3 }}>
            {SHEET_CARDS.map((card) => (
              <Grid item key={card.title} md={6} xs={12}>
                <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: "8px", minHeight: 72, p: 1.5 }}>
                  <Typography color={NAVY} fontSize={12} fontWeight={700} sx={{ alignItems: "center", display: "flex", gap: 0.7 }}>
                    <Box component="span" sx={{ color: "#527ba1", display: "inline-flex", "& svg": { fontSize: 16 } }}>{card.icon}</Box>{card.title}
                  </Typography>
                  <Typography color="#60758a" fontSize={10.5} sx={{ mt: 0.8 }}>{card.description}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Button
            disabled={loading || exporting || Boolean(error)}
            onClick={downloadExcel}
            startIcon={exporting ? <CircularProgress color="inherit" size={16} /> : <DownloadOutlinedIcon />}
            sx={{ backgroundColor: "#168a4d", color: "#fff", fontSize: 12, fontWeight: 700, mt: 2, px: 2.2, textTransform: "none", "&:hover": { backgroundColor: "#11723f" } }}
            variant="contained"
          >
            {exporting ? `Préparation de ${rows.length} fiche(s)...` : "Télécharger Excel"}
          </Button>
        </Paper>
        <Snackbar autoHideDuration={3500} message="Fichier Excel généré avec succès." onClose={() => setMessageOpen(false)} open={messageOpen} />
      </Box>
    </SMQMainContainer>
  );
};

export default ExportExcel;
